/**
 * Document model. See implementation-plan.md §3.
 *
 * One internal time unit: the signed integer year (negative = before the era),
 * including values like -3000000. `BandUnit` is a *formatting* concern only and
 * never reaches the layout engine.
 */

export type CategoryId = string;

/**
 * Display notation for a band's tick labels. Drawn from the source dataset,
 * which uses plain years, "ans AP" and "Ma AP" — and nothing else.
 */
export type BandUnit = 'year' | 'yearBP' | 'MaBP';

export interface Category {
  name: string;
  color: string;
}

export interface Band {
  label: string;
  start: number;
  end: number;
  /** Tick spacing in years. */
  step: number;
  /** Defaults to 'year'. */
  unit?: BandUnit;
  /** Forces a page break before this band when printing (plan §5.2). */
  pageBreakBefore?: boolean;
}

/**
 * A period runs between two dated years. Approximate bounds were modelled and
 * then dropped at the user's request: a hard bound that is slightly wrong reads
 * better than a fade that says "somewhere around here".
 */
export interface PeriodItem {
  type: 'period';
  cat: CategoryId;
  label: string;
  start: number;
  end: number;
  /** Pins the entry to a lane, overriding automatic stacking (features.md §5). */
  lane?: number;
}

/**
 * A point in time. `span` means the date itself is unknown within that range
 * (the Iliad, written somewhere in -850..-750) — the opposite of a duration,
 * and rendered as a whisker rather than a bar.
 */
export interface EventItem {
  type: 'event';
  cat: CategoryId;
  label: string;
  year: number;
  span?: [number, number];
  /** Pins the entry to a lane, overriding automatic stacking (features.md §5). */
  lane?: number;
}

export type Item = PeriodItem | EventItem;

export interface DocumentMeta {
  /** ISO 8601. Lets the UI tell dated copies apart on reopening (plan §3.3). */
  savedAt: string;
  appVersion: string;
}

export interface ChronoDocument {
  schemaVersion: number;
  meta: DocumentMeta;
  categories: Record<CategoryId, Category>;
  bands: Band[];
  items: Item[];
}

export function isPeriod(item: Item): item is PeriodItem {
  return item.type === 'period';
}

export function isEvent(item: Item): item is EventItem {
  return item.type === 'event';
}

/**
 * Maximal extent of an item in years, uncertainty included.
 *
 * Lane stacking uses this rather than the nominal dates, so an event's whisker
 * never collides with a neighbour (plan §3.2). The pixel padding a fade adds on
 * top of it is a rendering concern, applied in phase 4.
 */
export function itemExtent(item: Item): [start: number, end: number] {
  if (isPeriod(item)) {
    return [Math.min(item.start, item.end), Math.max(item.start, item.end)];
  }
  if (!item.span) return [item.year, item.year];
  return [
    Math.min(item.year, item.span[0], item.span[1]),
    Math.max(item.year, item.span[0], item.span[1]),
  ];
}
